import { imageFlip, turnPoint, type DamageStatus } from '@fsv/core';
import type { ResolvedDrawing } from '../drawings/useDrawing';
import './MiniDrawing.css';

export interface MiniPin {
  id: string;
  x: number;
  y: number;
  status: DamageStatus;
  highlight?: boolean;
}

/**
 * Static drawing with pins, for cards and the detail page. Two stacked SVGs
 * with the same viewBox: the lower one carries the image (so the dark-mode
 * blueprint filter applies to the lines only), the upper one the pins. Pins
 * are in drawing units, so a circle of radius r·width is a true circle; this
 * is the same aspect correction core's geometry applies to distances.
 *
 * Only the IMAGE is mirrored: a stored pin is in the frame of the view as it
 * appears on screen (core geometry), exactly as the board places it. Until
 * 17.10.2026 this placed pins at 1 − x on mirrored views, so a damage on the
 * bottom or right view sat on the other wing here than on the board.
 * `turned` shows the view the way somebody at the nose saw it (core
 * `turnPoint`/`imageFlip`), for the preview right after such a report.
 */
export function MiniDrawing({
  resolved,
  pins,
  radius,
  label,
  pinScale = 1,
  className,
  turned = false,
}: {
  resolved: ResolvedDrawing | null;
  pins: MiniPin[];
  /** Nearby radius preview: a fraction of the image width around a point. */
  radius?: { x: number; y: number; r: number } | undefined;
  label?: string;
  pinScale?: number;
  className?: string;
  turned?: boolean;
}) {
  if (!resolved) return <div className={`mini mini--loading${className ? ` ${className}` : ''}`} aria-hidden="true" />;
  const { drawing, mirrored } = resolved;
  const w = drawing.width;
  const h = drawing.height;
  const r = w * 0.02 * pinScale;
  const flip = imageFlip(mirrored, turned);
  const imageTransform =
    flip.scaleX === 1 && flip.scaleY === 1 ? undefined : `translate(${flip.scaleX < 0 ? w : 0} ${flip.scaleY < 0 ? h : 0}) scale(${flip.scaleX} ${flip.scaleY})`;
  const at = (p: { x: number; y: number }) => {
    const s = turnPoint(p, turned);
    return { cx: s.x * w, cy: s.y * h };
  };
  const centre = radius ? at(radius) : null;
  return (
    <div className={`mini${className ? ` ${className}` : ''}`} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <svg className="mini__layer mini__drawing" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
        <image href={drawing.url} width={w} height={h} transform={imageTransform} />
      </svg>
      <svg className="mini__layer" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
        {radius && centre && (
          <g className="mini__radius">
            <circle cx={centre.cx} cy={centre.cy} r={radius.r * w} />
            <circle className="mini__radius-center" cx={centre.cx} cy={centre.cy} r={r * 0.8} />
          </g>
        )}
        {pins
          .filter((p) => p.status !== 'verworfen')
          .sort((a, b) => Number(a.highlight ?? false) - Number(b.highlight ?? false))
          .map((p) => {
            const { cx, cy } = at(p);
            const rr = p.highlight ? r * 1.5 : r;
            return (
              <g key={p.id} className={`mini__pin st--${p.status}${p.highlight ? ' is-highlight' : ''}`}>
                {p.highlight && <circle className="mini__halo" cx={cx} cy={cy} r={rr * 2.2} />}
                {p.status === 'in_reparatur' ? (
                  <rect x={cx - rr} y={cy - rr} width={rr * 2} height={rr * 2} rx={rr * 0.4} />
                ) : (
                  <circle cx={cx} cy={cy} r={rr} />
                )}
                {p.status === 'beobachtung' && <circle className="mini__eye" cx={cx} cy={cy} r={rr * 0.4} />}
                {p.status === 'akzeptiert' && <rect className="mini__eye" x={cx - rr * 0.6} y={cy - rr * 0.12} width={rr * 1.2} height={rr * 0.24} />}
              </g>
            );
          })}
      </svg>
    </div>
  );
}
